import { type ReactNode, type Ref, useContext, useRef } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import type { DataGridOverflow } from "../columns/types";
import { CellTooltipHostContext } from "./CellTooltipHost";
import styles from "./cellContent.module.scss";

export type CellTextTone = "primary" | "secondary" | "tertiary";

interface CellTextProps {
    /** Full plain text; the tooltip content when the text is cut off. */
    text: string;
    overflow: DataGridOverflow;
    tone?: CellTextTone;
    /** Rich content for `text`, e.g. `HighlightedText`. Defaults to `text`. */
    children?: ReactNode;
    /** Shown on hover and focus whether or not the text is cut off (a timestamp's time). */
    tooltip?: string;
}

interface TextElementOptions {
    ref?: Ref<HTMLSpanElement>;
    /** The text a `CellTooltipHost` shows while this span is cut off. */
    tooltipText?: string;
    /** The text a `CellTooltipHost` always shows for this span. */
    alwaysTooltipText?: string;
}

/** A plain span, not a component: no extra fiber per cell, and `Tooltip` can merge trigger props onto it. */
const renderTextElement = (
    { text, overflow, tone = "primary", children }: Readonly<CellTextProps>,
    { ref, tooltipText, alwaysTooltipText }: TextElementOptions = {},
) => (
    <span
        ref={ref}
        className={styles.text}
        data-overflow={overflow}
        data-tone={tone}
        data-cell-tooltip={alwaysTooltipText ?? tooltipText}
        data-cell-tooltip-always={
            alwaysTooltipText === undefined ? undefined : ""
        }
    >
        {children ?? text}
    </span>
);

const TextWithOwnTooltip = (props: Readonly<CellTextProps>) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: textRef,
        isTruncated: isOverflowingX,
    });
    return (
        <Tooltip content={props.text} {...truncationTooltipProps}>
            {renderTextElement(props, { ref: textRef })}
        </Tooltip>
    );
};

/**
 * One run of cell text with the column's overflow rule: `truncate` (one line,
 * ellipsis, tooltip only while actually cut off), `wrap`, or `clip`. Sets its
 * own typography so it never depends on inherited styles. Inside a
 * `CellTooltipHost` the table's shared tooltip serves it; elsewhere it mounts
 * its own. A `tooltip` replaces the cut-off text and always shows.
 */
export const CellText = (props: Readonly<CellTextProps>) => {
    const hasTooltipHost = useContext(CellTooltipHostContext);
    if (props.tooltip !== undefined) {
        return hasTooltipHost ? (
            renderTextElement(props, { alwaysTooltipText: props.tooltip })
        ) : (
            <Tooltip content={props.tooltip}>
                {renderTextElement(props)}
            </Tooltip>
        );
    }
    if (props.overflow !== "truncate") {
        return renderTextElement(props);
    }
    return hasTooltipHost ? (
        renderTextElement(props, { tooltipText: props.text })
    ) : (
        <TextWithOwnTooltip {...props} />
    );
};
